// src/areaAssociados/CarrosselAvisos.js
import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaFilePdf } from 'react-icons/fa';
import api from '../services/api';

const CoresTipo = {
  aviso: { bg: '#e3f2fd', border: '#1976d2', text: '#1976d2' },
  alerta: { bg: '#fff3e0', border: '#f57c00', text: '#f57c00' },
  urgente: { bg: '#ffebee', border: '#d32f2f', text: '#d32f2f' },
  evento: { bg: '#e8f5e9', border: '#388e3c', text: '#388e3c' },
  info: { bg: '#e3f2fd', border: '#1976d2', text: '#1976d2' }
};

const CarrosselAvisos = ({ onNoticeClick }) => {
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);
  const autoScrollRef = useRef(null);
  const touchStart = useRef(null);
  const cardRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    carregarAvisos();
    
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    const handleNewNotice = (e) => {
      //console.log('📢 Carrossel: novo aviso via socket:', e.detail);
      carregarAvisos();
      setCurrentIndex(0);
    };
    
    window.addEventListener('notice:new', handleNewNotice);
    
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
      window.removeEventListener('notice:new', handleNewNotice);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    
    const handleTouchMove = (e) => {
      if (!touchStart.current) return;
      const dx = e.touches[0].clientX - touchStart.current.x;
      const dy = e.touches[0].clientY - touchStart.current.y;
      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      if (!touchStart.current) return;
      const dx = e.changedTouches[0].clientX - touchStart.current.x;
      const dy = e.changedTouches[0].clientY - touchStart.current.y;
      touchStart.current = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goToNext();
        else goToPrev();
      }
    };
    
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [avisos.length]);

  const carregarAvisos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notices/public');
      if (response.data.success && response.data.notices) {
        const filtered = response.data.notices.filter(n => n.tipo !== 'evento');
        const sorted = filtered.sort((a, b) => {
          const aDate = a.eventoData ? new Date(a.eventoData) : (a.expiraEm ? new Date(a.expiraEm) : new Date(a.createdAt || 0));
          const bDate = b.eventoData ? new Date(b.eventoData) : (b.expiraEm ? new Date(b.expiraEm) : new Date(b.createdAt || 0));
          return aDate - bDate;
        });
        setAvisos(sorted);
      }
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrev = (e) => {
    e?.stopPropagation();
    resetAutoScroll();
    setCurrentIndex(prev => (prev - 1 + avisos.length) % avisos.length);
  };

  const goToNext = (e) => {
    e?.stopPropagation();
    resetAutoScroll();
    setCurrentIndex(prev => (prev + 1) % avisos.length);
  };

  const resetAutoScroll = (total = avisos.length) => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
  };

  if (loading || avisos.length === 0) {
    return null;
  }

  const aviso = avisos[currentIndex];
  const cores = CoresTipo[aviso.tipo] || CoresTipo.aviso;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>📢 Avisos</span>
        <span style={styles.counter}>{currentIndex + 1}/{avisos.length}</span>
      </div>
      
      <div style={styles.carrosselWrapper}>
        {!isMobile && avisos.length > 1 && (
          <button style={styles.navButton} onClick={goToPrev}>
            <FaChevronLeft size={16} />
          </button>
        )}
        
        <div 
          ref={cardRef}
          style={{
            ...styles.card,
            borderLeft: `4px solid ${cores.border}`,
            backgroundColor: cores.bg
          }}
          onClick={() => onNoticeClick && onNoticeClick(aviso)}
          onTouchStart={(e) => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
        >
          {aviso.imagemUrl && (
            <div style={styles.cardImage}>
              <img 
                src={aviso.imagemUrl} 
                alt={aviso.titulo}
                style={styles.cardImg}
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          
          <div style={styles.cardContent}>
            <div style={styles.cardHeader}>
              <span style={{
                ...styles.tipoBadge,
                backgroundColor: 'white',
                color: cores.text
              }}>
                {aviso.tipoLabel}
              </span>
              {aviso.prioridade === 'alta' && (
                <span style={styles.prioridadeBadge}>⚡ priority</span>
              )}
              {(aviso.eventoData || aviso.expiraEm) && (
                <span style={{
                  color: cores.text,
                  fontSize: '11px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  marginLeft: 'auto',
                  textAlign: 'right'
                }}>
                  {aviso.eventoData 
                    ? `${new Date(aviso.eventoData).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} ${new Date(aviso.eventoData).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                    : `Até ${new Date(aviso.expiraEm).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`
                  }
                </span>
              )}
            </div>
            
            <h3 style={{...styles.titulo, color: cores.text}}>
              {aviso.titulo}
            </h3>
            
            {aviso.mensagem && (
              <p style={{ ...styles.mensagem, whiteSpace: 'pre-line' }}>
                {aviso.mensagem.length > 120
                  ? aviso.mensagem.substring(0, 120) + '...'
                  : aviso.mensagem}
              </p>
            )}
            
            {aviso.eventoTitulo && (
              <div style={styles.eventoBadge}>
                📅 {aviso.eventoTitulo}
              </div>
            )}
            
            {aviso.pdfUrl && (
              <a 
                href={aviso.pdfUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '6px',
                  padding: '6px 10px',
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#c62828',
                  textDecoration: 'none',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                <FaFilePdf size={14} /> Abrir documento PDF
              </a>
            )}
          </div>
        </div>
        
        {!isMobile && avisos.length > 1 && (
          <button style={styles.navButton} onClick={goToNext}>
            <FaChevronRight size={16} />
          </button>
        )}
      </div>
      
      {avisos.length > 1 && (
        <div style={styles.dots}>
          {avisos.map((_, idx) => (
            <span 
              key={idx}
              style={{
                ...styles.dot,
                backgroundColor: idx === currentIndex ? '#1A3B7D' : '#ccc'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
                resetAutoScroll();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '15px',
    marginBottom: '20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid #eee'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  title: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1A3B7D',
    fontFamily: 'Montserrat, sans-serif'
  },
  counter: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'Montserrat, sans-serif'
  },
  carrosselWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  navButton: {
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    color: '#666'
  },
  card: {
    flex: 1,
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'pan-y'
  },
  cardImage: {
    width: '100%',
    height: '130px',
    overflow: 'hidden'
  },
  cardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  cardContent: {
    flex: 1,
    padding: '16px',
    minWidth: 0,
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px'
  },
  tipoBadge: {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: '600',
    fontFamily: 'Montserrat, sans-serif'
  },
  prioridadeBadge: {
    fontSize: '10px',
    color: '#ff9800',
    fontWeight: '700',
    fontFamily: 'Montserrat, sans-serif'
  },
  titulo: {
    fontSize: '15px',
    fontWeight: '700',
    marginBottom: '4px',
    fontFamily: 'Montserrat, sans-serif',
    lineHeight: '1.3'
  },
  mensagem: {
    fontSize: '12px',
    color: '#666',
    fontFamily: 'Montserrat, sans-serif',
    lineHeight: '1.5',
    margin: 0,
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical'
  },
  eventoBadge: {
    fontSize: '11px',
    color: '#388e3c',
    backgroundColor: '#e8f5e9',
    padding: '3px 8px',
    borderRadius: '10px',
    marginTop: '6px',
    display: 'inline-block',
    fontFamily: 'Montserrat, sans-serif'
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '12px'
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    cursor: 'pointer'
  }
};

export default CarrosselAvisos;